/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ButtonProps,
  DIALOG_SIZE_SM_MD,
  DialogContentProps,
  DialogProps,
  ICON_ADD_CIRCLE,
  useDialogStore,
} from '@squiz/sds';
import { DialogLauncher } from 'apps/console-core/src/Components/DialogLauncher/DialogLauncher';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { isGenerative } from '../../Utilities/formPredicates';
import { CreateToolDialogProps } from './CreateToolDialog.props';
import { CreateToolDialogForm } from './CreateToolDialogForm/CreateToolDialogForm';

/**
 * The button for editing a content tool.
 * @param {CreateToolDialogProps} props Props passed to `<CreateToolDialog />`.
 * @returns {JSX.Element} The `<CreateToolDialog />` component.
 */
export const CreateToolDialog = (props: CreateToolDialogProps): JSX.Element | undefined => {
  const { t } = useTranslation();

  const canCreate = isGenerative(props.type)
    ? hasPermission(PERMISSIONS.dxpAi.GENERATIVE_WRITE)
    : hasPermission(PERMISSIONS.dxpAi.CHATBOT_WRITE);

  const labels = useMemo(() => {
    return {
      createChatbot: t(AI_STRINGS.createChatbot),
      createChatbotDesc: t(AI_STRINGS.createChatbotDialogDescription),
      createTool: t(AI_STRINGS.createTool),
      createToolDesc: t(AI_STRINGS.createToolDialogDescription),
    };
  }, [t]);

  const dialogContent = isGenerative(props.type)
    ? {
        heading: labels.createTool,
        subtitle: labels.createToolDesc,
      }
    : {
        heading: labels.createChatbot,
        subtitle: labels.createChatbotDesc,
      };

  const dialogProps: DialogProps = {
    classes: 'create-tool-dialog',
    dialogContent: {
      renderContent: (dialogProps: DialogContentProps): JSX.Element => {
        return <CreateToolDialogForm {...dialogProps} type={props.type} />;
      },
    },
    dialogSize: DIALOG_SIZE_SM_MD,
    heading: dialogContent.heading,
    icon: ICON_ADD_CIRCLE,
    showDividers: false,
    stateHandler: useDialogStore,
    subtitle: dialogContent.subtitle,
  };

  const buttonProps: ButtonProps = {
    ...props.buttonProps,
    text: dialogContent.heading,
  };

  if (!canCreate) {
    return undefined;
  }

  return (
    <div>
      <DialogLauncher buttonProps={buttonProps} dialogProps={dialogProps} />
    </div>
  );
};
